<script lang="ts">
	import { locale, localizeHref } from '$lib/localization/locale';
	import { message } from '$lib/localization/messages';

	import { createDialog } from 'svelte-headlessui';
	import { signOut, type User } from 'firebase/auth';
	import { auth } from '$lib/firebase';
	import authStore from '$lib/authStore';
	import { analytics } from '$lib/analytics';
	import Transition from 'svelte-transition';
	import LightDarkModeSwitcher from './LightDarkModeSwitcher.svelte';
	import NavigationPreference from './NavigationPreference.svelte';
	import Bars from '$components/inline-svg/Bars.svelte';
	import { onMount, onDestroy } from 'svelte';
	import PlanInformationClient from '$lib/planInformationClient';
	import type { PlanInformation } from '$lib/diffusionApi';
	import { logLandingBoot } from '$lib/landingBootLog';
	import { IS_AQUEDUCT } from '$lib/site';
	import { DISCORD_URL } from '$lib/siteConfig';

	logLandingBoot('Header: component script evaluated');

	export let fixed: boolean = false;
	export let matchCanvasUi: boolean = false;

	export let user: User | undefined | null = undefined;
	export let journeyId: string | undefined = undefined;
	export let userIsOwner: boolean | undefined = undefined;

	const dialog = createDialog({ label: 'Menu' });

	let planInformation: PlanInformation | undefined = undefined;
	let planClient: PlanInformationClient = new PlanInformationClient('');
	let subscriptions: (() => void)[] = [];

	async function logout(): Promise<void> {
		try {
			await signOut(auth);
			analytics.reset();
			$dialog.expanded = false;
		} catch (error: unknown) {
			console.log(error);
		}
	}

	function handleWindowPointerDown(e: PointerEvent) {
		if (!$dialog.expanded) return;
		if (!e.target) return;
		if (!(e.target as HTMLElement).closest('#header-menu')) {
			$dialog.expanded = false;
		}
	}

	onMount(() => {
		const startedAt = performance.now();
		logLandingBoot('Header: onMount start');
		subscriptions.push(
			authStore.subscribe(async (auth) => {
				logLandingBoot('Header: authStore update', {
					hasUser: !!auth.user,
					isLoggedIn: auth.isLoggedIn,
					firebaseControlled: auth.firebaseControlled
				});
				if (auth.user === undefined || auth.user === null || IS_AQUEDUCT) {
					planInformation = undefined;
					return;
				}

				try {
					const planStartedAt = performance.now();
					planClient.setToken((await auth.user.getIdToken()) ?? '');
					planInformation = await planClient.getPlanInformation();
					logLandingBoot('Header: plan information loaded', {
						duration: Number((performance.now() - planStartedAt).toFixed(1)),
						planName: planInformation?.name
					});
					if (planInformation) {
						analytics.setPersonProperties({ planName: planInformation.name });
					}
				} catch (error) {
					console.error('Failed to fetch plan information:', error);
					planInformation = undefined;
				}
			})
		);
		logLandingBoot('Header: onMount complete', {
			duration: Number((performance.now() - startedAt).toFixed(1)),
			subscriptions: subscriptions.length
		});
	});

	onDestroy(() => {
		for (const unsubscribe of subscriptions) {
			unsubscribe();
		}
	});
</script>

<svelte:window on:pointerdown={handleWindowPointerDown} />

<div id="header-menu" class="flex flex-row z-20">
	<div class="flex flex-row justify-start items-center">
		<button
			aria-label={message($locale, 'common/Header', 'menu')}
			aria-expanded={$dialog.expanded}
			class="w-8 h-8 flex flex-row justify-center items-center text-base rounded shadow-md
			text-primary-600 bg-primary-50 border border-primary-200 hover:brightness-90
			dark:text-zinc-400 dark:border-zinc-800 dark:hover:border-zinc-700 dark:bg-navy-900 dark:hover:bg-navy-800 dark:hover:text-zinc-300 dark:shadow-sm"
			on:click={() => {
				$dialog.expanded = !$dialog.expanded;
			}}
		>
			<Bars customClass={matchCanvasUi ? 'text-zinc-900 dark:text-zinc-400' : ''} />
		</button>
		<Transition show={$dialog.expanded}>
			<div
				class="{fixed
					? 'fixed'
					: 'absolute'} z-20 top-10 left-2 w-52 h-fit max-h-[calc(100dvh-56px)] overflow-y-auto scroller flex flex-col justify-start items-center
				text-zinc-800 bg-primary-50 rounded border border-primary-200 text-xs shadow-md
				dark:text-zinc-100 dark:bg-navy-900 dark:border-zinc-800"
			>
				<div class="h-1.5"></div>
				{#if $authStore.isLoggedIn}
					<div class="w-[196px] h-[4rem] py-2 px-1 overflow-x-hidden overflow-y-hidden">
						{message($locale, 'common/Header', 'signedInAs')}
						<div class="h-0.5"></div>
						<p>
							<span class="font-semibold"
								>{$authStore.user?.displayName ? $authStore.user?.displayName : ''}</span
							>
						</p>
						<p>{$authStore.user?.email ?? ''}</p>
					</div>
				{:else}
					<div
						class="w-[196px] h-fit bg-primary-50 rounded hover:brightness-90
						dark:bg-navy-900 dark:hover:brightness-150"
					>
						<a
							href={localizeHref($locale, '/login')}
							class="flex flex-row justify-start items-center w-full py-2 px-1"
						>
							<div class="w-6 flex flex-row justify-center items-center">
								<i class="fa-solid fa-sign-in text-zinc-800 dark:text-zinc-200"></i>
							</div>
							<div class="w-1.5"></div>
							{message($locale, 'common/Header', 'logIn')}
						</a>
					</div>
				{/if}
				<div class="h-1.5"></div>
				<div class="w-full h-0 border-t border-primary-200 dark:border-zinc-800"></div>
				<div class="h-1.5"></div>
				<div
					class="w-[196px] h-fit bg-primary-50 rounded hover:brightness-90
					dark:bg-navy-900 dark:hover:brightness-150"
				>
					<a
						href={localizeHref($locale, '/')}
						class="flex flex-row justify-start items-center w-full py-2 px-1"
					>
						<div class="w-6 flex flex-row justify-center items-center">
							<i class="fa-solid fa-house text-zinc-600 dark:text-zinc-200"></i>
						</div>
						<div class="w-1.5"></div>
						{message($locale, 'common/Header', 'home')}
					</a>
				</div>
				<div class="h-1.5"></div>
				<div class="w-full h-0 border-t border-primary-200 dark:border-zinc-800"></div>
				<div class="h-1.5"></div>
				{#if IS_AQUEDUCT}
					<div
						class="w-[196px] h-fit bg-primary-50 rounded hover:brightness-90
							dark:bg-navy-900 dark:hover:brightness-150"
					>
						<a
							href={localizeHref($locale, $authStore.isLoggedIn ? '/start' : '/projects')}
							class="flex flex-row justify-start items-center w-full py-2 px-1"
						>
							<div class="w-6 flex flex-row justify-center items-center">
								<i class="fa-solid fa-droplet text-cyan-600 dark:text-cyan-400"></i>
							</div>
							<div class="w-1.5"></div>
							{message($locale, 'common/Header', 'startHere')}
						</a>
					</div>
					<div class="h-1.5"></div>
					<div
						class="w-[196px] h-fit bg-primary-50 rounded hover:brightness-90
						dark:bg-navy-900 dark:hover:brightness-150"
					>
						<a
							href={localizeHref($locale, '/projects')}
							class="flex flex-row justify-start items-center w-full py-2 px-1"
						>
							<div class="w-6 flex flex-row justify-center items-center">
								<i class="fa-solid fa-diagram-project text-cyan-600 dark:text-cyan-400"></i>
							</div>
							<div class="w-1.5"></div>
							{message($locale, 'common/Header', 'projects')}
						</a>
					</div>
					<div class="h-1.5"></div>
					<div
						class="w-[196px] h-fit bg-primary-50 rounded hover:brightness-90
						dark:bg-navy-900 dark:hover:brightness-150"
					>
						<a
							href={localizeHref($locale, '/docs')}
							class="flex flex-row justify-start items-center w-full py-2 px-1"
						>
							<div class="w-6 flex flex-row justify-center items-center">
								<i class="fa-solid fa-book-open text-cyan-600 dark:text-cyan-400"></i>
							</div>
							<div class="w-1.5"></div>
							{message($locale, 'common/Header', 'docs')}
						</a>
					</div>
					<div class="h-1.5"></div>
				{:else}
					<div
						class="w-[196px] h-fit bg-primary-50 rounded hover:brightness-90
					dark:bg-navy-900 dark:hover:brightness-150"
					>
						<a
							href={localizeHref($locale, '/canvas')}
							class="flex flex-row justify-start items-center w-full py-2 px-1"
						>
							<div class="w-6 flex flex-row justify-center items-center">
								<i class="fa-solid fa-paintbrush text-cyan-600 dark:text-cyan-400"></i>
							</div>
							<div class="w-1.5"></div>
							Texture Canvas
						</a>
					</div>
					<div class="h-1.5"></div>
					<div
						class="w-[196px] h-fit bg-primary-50 rounded hover:brightness-90
					dark:bg-navy-900 dark:hover:brightness-150"
					>
						<a
							href={localizeHref($locale, '/material-generator')}
							class="flex flex-row justify-start items-center w-full py-2 px-1"
						>
							<div class="w-6 flex flex-row justify-center items-center">
								<i class="fa-solid fa-cube text-cyan-600 dark:text-cyan-400"></i>
							</div>
							<div class="w-1.5"></div>
							Material Generator
						</a>
					</div>
					<div class="h-1.5"></div>
					<div
						class="w-[196px] h-fit bg-primary-50 rounded hover:brightness-90
					dark:bg-navy-900 dark:hover:brightness-150"
					>
						<a
							href={localizeHref($locale, '/reference-to-texture')}
							class="flex flex-row justify-start items-center w-full py-2 px-1"
						>
							<div class="w-6 flex flex-row justify-center items-center">
								<i class="fa-solid fa-arrow-up-from-bracket text-cyan-600 dark:text-cyan-400"></i>
							</div>
							<div class="w-1.5"></div>
							Reference to Texture
						</a>
					</div>
					<div class="h-1.5"></div>
					<div class="w-full h-0 border-t border-primary-200 dark:border-zinc-800"></div>
					<div class="h-1.5"></div>
					<div
						class="w-[196px] h-fit bg-primary-50 rounded hover:brightness-90
					dark:bg-navy-900 dark:hover:brightness-150"
					>
						<a
							href={localizeHref($locale, '/library')}
							class="flex flex-row justify-start items-center w-full py-2 px-1"
						>
							<div class="w-6 flex flex-row justify-center items-center">
								<i class="fa-solid fa-images text-rose-500 dark:text-rose-400"></i>
							</div>
							<div class="w-1.5"></div>
							Library
						</a>
					</div>
					<!-- <div
					class="w-[196px] h-fit bg-primary-50 rounded hover:brightness-90
					dark:bg-navy-900 dark:hover:brightness-150"
				>
					<a href="/library-3d" class="flex flex-row justify-start items-center w-full py-2 px-1">
						<div class="w-6 flex flex-row justify-center items-center">
							<i class="fa-solid fa-cubes text-rose-500 dark:text-rose-400"></i>
						</div>
						<div class="w-1.5"></div>
						3D Library
					</a>
				</div> -->
					<!-- <div
					class="w-[196px] h-fit bg-primary-50 rounded hover:brightness-90
					dark:bg-navy-900 dark:hover:brightness-150"
				>
					<a href="/boards" class="flex flex-row justify-start items-center w-full py-2 px-1">
						<div class="w-6 flex flex-row justify-center items-center">
							<i class="fa-solid fa-thumbtack text-rose-500 dark:text-rose-400"></i>
						</div>
						<div class="w-1.5"></div>
						Boards
					</a>
				</div> -->
					<div class="h-1.5"></div>
					<!-- <div class="w-full h-0 border-t border-primary-200 dark:border-zinc-800"></div>
				<div class="h-1.5"></div>
				<div
					class="w-[196px] h-fit bg-primary-50 rounded hover:brightness-90
					dark:bg-navy-900 dark:hover:brightness-150"
				>
					<a href="/journeys" class="flex flex-row justify-start items-center w-full py-2 px-1">
						<div class="w-6 flex flex-row justify-center items-center">
							<i class="fa-solid fa-sailboat text-stone-500 dark:text-[#dab488]"></i>
						</div>
						<div class="w-1.5"></div>
						Journeys
					</a>
				</div>
				<div class="h-1.5"></div> -->
				{/if}
				<div class="w-full h-0 border-t border-primary-200 dark:border-zinc-800"></div>
				<div class="h-1.5"></div>
				<!-- <div
					class="w-[196px] h-fit bg-primary-50 rounded hover:brightness-90
				dark:bg-navy-900 dark:hover:brightness-150"
				>
					<a href="/utilities" class="flex flex-row justify-start items-center w-full py-2 px-1">
						<div class="w-6 flex flex-row justify-center items-center">
							<i class="fa-solid fa-screwdriver-wrench text-teal-600 dark:text-teal-400"></i>
						</div>
						<div class="w-1.5"></div>
						Utilities
					</a>
				</div>
				<div class="h-1.5"></div>
				<div class="w-full h-0 border-t border-primary-200 dark:border-zinc-800"></div>
				<div class="h-1.5"></div> -->
				<div
					class="w-[196px] h-fit bg-primary-50 rounded hover:brightness-90
					dark:bg-navy-900 dark:hover:brightness-150"
				>
					<a
						href={localizeHref($locale, DISCORD_URL)}
						class="flex flex-row justify-start items-center w-full py-2 px-1"
						on:click={(e) => {
							e.preventDefault();
							analytics.capture('click_header_discord_link', {
								user_id: user?.uid,
								journey_id: journeyId,
								is_owner: userIsOwner
							});
							window.open(DISCORD_URL, '_blank');
						}}
					>
						<div class="w-6 flex flex-row justify-center items-center">
							<i class="fa-brands fa-discord text-[#6d7dcd]"></i>
						</div>
						<div class="w-1.5"></div>
						{IS_AQUEDUCT
							? message($locale, 'common/Header', 'joinDiscordCommunity')
							: 'Iliad Discord'}
					</a>
				</div>
				<div class="h-1.5"></div>
				<div class="w-full h-0 border-t border-primary-200 dark:border-zinc-800"></div>
				<div class="h-1.5"></div>
				<div
					class="w-[196px] h-fit bg-primary-50 rounded hover:brightness-90
					dark:bg-navy-900 dark:hover:brightness-150"
				>
					<a
						href={localizeHref($locale, '/account-settings')}
						class="flex flex-row justify-start items-center w-full py-2 px-1"
					>
						<div class="w-6 flex flex-row justify-center items-center">
							<i class="fa-solid fa-user-gear text-emerald-600 dark:text-emerald-400"></i>
						</div>
						<div class="w-1.5"></div>
						{message($locale, 'common/Header', 'accountSettings')}
					</a>
				</div>
				{#if IS_AQUEDUCT || !$authStore.isLoggedIn || (planInformation && planInformation.name === 'Starter')}
					<div
						class="w-[196px] h-fit bg-primary-50 rounded hover:brightness-90
						dark:bg-navy-900 dark:hover:brightness-150"
					>
						<a
							href={localizeHref($locale, IS_AQUEDUCT ? '/pricing' : '/plan')}
							class="flex flex-row justify-start items-center w-full py-2 px-1"
						>
							<div class="w-6 flex flex-row justify-center items-center">
								<i class="fa-solid fa-tags text-emerald-600 dark:text-emerald-400"></i>
							</div>
							<div class="w-1.5"></div>
							{message($locale, 'common/Header', 'pricing')}
						</a>
					</div>
				{/if}
				<div class="h-1.5"></div>
				<div class="w-full h-0 border-t border-primary-200 dark:border-zinc-800"></div>
				<div class="w-[196px] py-1.5 flex flex-col">
					<LightDarkModeSwitcher />
					{#if IS_AQUEDUCT}<NavigationPreference />{/if}
				</div>
				{#if $authStore.isLoggedIn}
					<div class="w-full h-0 border-t border-primary-200 dark:border-zinc-800"></div>
					<div class="h-1.5"></div>
					<div
						class="w-[196px] h-fit bg-primary-50 rounded hover:brightness-90
						dark:bg-navy-900 dark:hover:brightness-150"
					>
						<button
							type="button"
							class="flex flex-row justify-start items-center w-full py-2 px-1"
							on:click={logout}
						>
							<div class="w-6 flex flex-row justify-center items-center">
								<i class="fa-solid fa-sign-out text-zinc-600 dark:text-zinc-200"></i>
							</div>
							<div class="w-1.5"></div>
							{message($locale, 'common/Header', 'signOut')}
						</button>
					</div>
					<div class="h-1.5"></div>
				{/if}
			</div>
		</Transition>
	</div>
</div>
